import React from 'react';
import { Card, Typography, Spinner } from "@material-tailwind/react";

const TransactionTypeMatrix = ({ typeMatrixData, loading = false }) => {
    const formatCurrency = (value) => {
        if (!value || value === 0) return '-';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    if (loading) {
        return (
            <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="text-center py-8">
                    <Spinner className="h-8 w-8 mx-auto text-brand-primary" />
                    <Typography variant="small" color="gray" className="mt-2">
                        Loading transaction type totals...
                    </Typography>
                </div>
            </Card>
        );
    }

    if (!typeMatrixData || !Array.isArray(typeMatrixData) || typeMatrixData.length === 0) {
        return (
            <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <Typography variant="h6" color="blue-gray" className="mb-4 text-brand-secondary">
                    Transaction Type Totals
                </Typography>
                <div className="text-center py-8">
                    <Typography variant="small" color="gray">
                        No transaction type data available for this week
                    </Typography>
                </div>
            </Card>
        );
    }

    // Calculate total for percentage calculations
    const grandTotal = typeMatrixData.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);

    return (
        <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
            <Typography variant="h6" color="blue-gray" className="mb-4 text-brand-secondary">
                Transaction Type Totals
            </Typography>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {typeMatrixData.map((item, index) => {
                    const amount = parseFloat(item.total_amount || 0);
                    const percentage = grandTotal !== 0 ? (Math.abs(amount) / Math.abs(grandTotal)) * 100 : 0;

                    return (
                        <div key={index} className="bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 rounded-lg p-4 border border-brand-primary/20">
                            <div className="text-center">
                                <Typography variant="small" color="blue-gray" className="font-medium mb-1">
                                    {item.name || item.trans_type}
                                </Typography>
                                <Typography variant="small" color="gray" className="text-xs mb-2 uppercase">
                                    {item.trans_type}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    className={`font-bold ${amount > 0 ? 'text-green-600' : amount < 0 ? 'text-red-600' : 'text-gray-500'}`}
                                >
                                    {formatCurrency(amount)}
                                </Typography>
                                <Typography variant="small" color="gray" className="text-xs mt-1">
                                    {percentage.toFixed(1)}% of total
                                </Typography>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                    <Typography variant="small" color="gray">
                        Total ({typeMatrixData.length} transaction types)
                    </Typography>
                    <Typography
                        variant="h6"
                        className={`font-bold ${grandTotal > 0 ? 'text-green-600' : grandTotal < 0 ? 'text-red-600' : 'text-gray-500'}`}
                    >
                        {formatCurrency(grandTotal)}
                    </Typography>
                </div>
            </div>

            <div className="mt-4 text-xs text-gray-600">
                <p>
                    <strong>Transaction Type Totals:</strong> This shows the total amount for each transaction type during the selected week.
                    Green values indicate positive amounts (income), red indicates negative amounts (expenses).
                </p>
            </div>
        </Card>
    );
};

export default TransactionTypeMatrix;